import React, { useState, useEffect } from 'react';
import twilioService, { TwilioSubaccount } from '../../services/twilioService';
import { useOrganization } from '../../pages/Organization/OrganizationContext';
import { useAuth } from '../../context/Auth';

const TwilioSubaccountComponent: React.FC = () => {
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subaccount, setSubaccount] = useState<TwilioSubaccount | null>(null);
  const [friendlyName, setFriendlyName] = useState('');

  const { fetchUser } = useAuth();
  useEffect(() => {
    if (organization?._id) {
      fetchSubaccount();
    }
  }, [organization]);

  const fetchSubaccount = async () => {
    if (!organization?._id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await twilioService.getSubaccount(organization._id);
      setSubaccount(data);
    } catch (err) {
      setError('Failed to fetch subaccount information');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createSubaccount = async () => {
    if (!organization?._id || !friendlyName) return;

    setCreating(true);
    setError(null);

    try {
      await twilioService.createSubaccount(organization._id, friendlyName);
      await fetchUser();
    } catch (err) {
      setError('Failed to create subaccount');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold">Twilio Subaccount</h2>
      </div>
      <hr className="border-gray-200" />
      <div className="p-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : subaccount ? (
          <div>
            <h3 className="text-lg font-bold">Subaccount Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <p className="text-sm text-gray-600">Friendly Name:</p>
                <p className="text-base">{subaccount.friendlyName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">SID:</p>
                <p className="text-base">{subaccount.sid}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status:</p>
                <p className="text-base">{subaccount.status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date Created:</p>
                <p className="text-base">
                  {new Date(subaccount.dateCreated).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="mb-4">
              You don't have a Twilio subaccount set up yet. Create one below:
            </p>

            <form className="space-y-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Friendly Name
                </label>
                <input
                  type="text"
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={createSubaccount}
                disabled={creating || !friendlyName}
                className={`flex items-center px-4 py-2 rounded-md text-white font-medium ${creating || !friendlyName
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  'Create Subaccount'
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwilioSubaccountComponent; 